import {
  ArrowDownIcon,
  ArrowTopRightOnSquareIcon,
  StarIcon,
  TrophyIcon,
} from '@heroicons/react/20/solid';
import { QrCodeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

import { ShiftDetailActivity, ShiftDetailWorker } from '../api/types';
import useShiftDetail from '../hooks/useShiftDetail';

import Avatar from './Avatar';

// Shift Detail side-panel, opened when a shift card is clicked. Docked on the
// right; the X (or a click on the backdrop) closes it. Pixel-transcribed from
// the Figma (Dashboard-shift-details).

const clockRange = (
  start?: string | null,
  end?: string | null,
  tz?: string | null,
): string => {
  if (!start || !end) return '';
  const fmt = (iso: string) =>
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz || 'America/New_York',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(iso));
  return `${fmt(start)} - ${fmt(end)}`;
};

const relativeDay = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const isSameDay = d.toDateString() === today.toDateString();
  if (isSameDay) return 'Today';
  const yest = new Date(today);
  yest.setDate(today.getDate() - 1);
  if (d.toDateString() === yest.toDateString()) return 'Yesterday';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(d);
};

const WorkerRow = ({ worker }: { worker: ShiftDetailWorker }) => (
  <div className="dashv2-panel-worker">
    <Avatar name={worker.name} src={worker.avatar_url} />
    <div className="dashv2-panel-workermeta">
      <span className="dashv2-panel-workername">
        {worker.name || 'Unknown worker'}
      </span>
      <div className="dashv2-panel-workertags">
        <span
          className={`dashv2-panel-dep ${worker.below_gate ? 'is-below' : 'is-qualifying'}`}
        >
          {worker.below_gate ? <ArrowDownIcon /> : <StarIcon />}
          Dependability: {worker.dependability_score}
        </span>
        {worker.hired && <span className="dashv2-panel-hiredtag">Hired</span>}
      </div>
    </div>
  </div>
);

const ActivityItem = ({ item }: { item: ShiftDetailActivity }) => (
  <div className="dashv2-panel-actitem">
    <div className="dashv2-panel-actrow">
      <Avatar name={item.title} src={item.avatar_url} />
      <span className="dashv2-panel-acttext">
        {item.title || item.event_type}
      </span>
    </div>
    <div className="dashv2-panel-actmeta">
      <span>{relativeDay(item.occurred_at)}</span>
      <span className="dashv2-panel-dot" />
      <span>{item.event_type}</span>
    </div>
  </div>
);

type Props = {
  agencyId: number;
  shiftId: number;
  onClose: () => void;
};

const ShiftDetailPanel = ({ agencyId, shiftId, onClose }: Props) => {
  const { data, isLoading, isError } = useShiftDetail(agencyId, shiftId);
  const jobHref = data?.order_id
    ? `/orders/${data.order_id}/jobs/${data.job_id}`
    : '#';
  const address = data
    ? [
        data.address_line1,
        [data.city, data.state, data.zip].filter(Boolean).join(', '),
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  return (
    <>
      <div className="dashv2-panel-overlay" onClick={onClose} />
      <aside
        className="dashv2-panel"
        role="complementary"
        aria-label="Shift detail"
      >
        {isLoading && !data ? (
          <div className="dashv2-panel-loading">Loading…</div>
        ) : isError || !data ? (
          <>
            <div className="dashv2-panel-headrow">
              <div className="dashv2-panel-headmain" />
              <button
                className="dashv2-panel-close"
                aria-label="Close"
                onClick={onClose}
              >
                <XMarkIcon />
              </button>
            </div>
            <div className="dashv2-panel-loading">
              Unable to load shift detail.
            </div>
          </>
        ) : (
          <>
            {/* Header */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="dashv2-panel-headrow">
                <div className="dashv2-panel-headmain">
                  <span className="dashv2-panel-logo">
                    {data.logo_url ? <img src={data.logo_url} alt="" /> : null}
                  </span>
                  <h2 className="dashv2-panel-title">
                    {data.client_name || data.venue_name || 'Shift'}
                  </h2>
                </div>
                <button
                  className="dashv2-panel-close"
                  aria-label="Close"
                  onClick={onClose}
                >
                  <XMarkIcon />
                </button>
              </div>
              {data.status_label && (
                <span className="dashv2-panel-status">{data.status_label}</span>
              )}
            </div>

            <div className="dashv2-panel-actions">
              <Link className="dashv2-panel-btn is-primary" to={jobHref}>
                View job
              </Link>
              <Link className="dashv2-panel-btn is-secondary" to={jobHref}>
                View QR
                <QrCodeIcon />
              </Link>
            </div>

            {/* Gate + classification */}
            <div className="dashv2-panel-gaterow">
              <span className="dashv2-panel-chip">
                <TrophyIcon style={{ color: 'var(--sec-yellow)' }} />
                Dependability gate: {data.dependability_gate_label}
              </span>
              {data.worker_classification && (
                <span className="dashv2-panel-chip">
                  {data.worker_classification}
                </span>
              )}
            </div>

            {/* Details */}
            <div className="dashv2-panel-details">
              <div className="dashv2-panel-jobblock">
                <Link className="dashv2-panel-jobid" to={jobHref}>
                  Job ID: {data.job_id}
                  <ArrowTopRightOnSquareIcon />
                </Link>
                <div className="dashv2-panel-shiftinfo">
                  <div className="dashv2-panel-hours">
                    <span>
                      {clockRange(data.start_at, data.end_at, data.timezone)}
                    </span>
                    {data.duration_hours != null && (
                      <>
                        <span className="dashv2-panel-dot" />
                        <span>{data.duration_hours} hrs</span>
                      </>
                    )}
                  </div>
                  {data.role && (
                    <p className="dashv2-panel-role">{data.role}</p>
                  )}
                  {address && <p className="dashv2-panel-address">{address}</p>}
                </div>
              </div>

              {data.supervisor?.name && (
                <div className="dashv2-panel-supervisor">
                  <span className="dashv2-panel-suptitle">
                    Shift supervisor
                  </span>
                  <span className="dashv2-panel-supname">
                    {data.supervisor.name}
                  </span>
                  {data.supervisor.phone && (
                    <span className="dashv2-panel-supphone">
                      {data.supervisor.phone}
                    </span>
                  )}
                </div>
              )}

              <div className="dashv2-panel-divider" />

              <div>
                <div className="dashv2-panel-fillrow">
                  <span className="dashv2-panel-fillcount">
                    {data.hired_count} of {data.quantity} hired
                  </span>
                  <span className="dashv2-panel-fillbadge">
                    {data.percent_filled}% filled
                  </span>
                </div>
                {(data.workers ?? []).map((w) => (
                  <WorkerRow key={w.worker_id ?? w.name} worker={w} />
                ))}
                <button type="button" className="dashv2-panel-cta">
                  Increase pay rate
                </button>
              </div>
            </div>

            {/* Recent activity */}
            {(data.recent_activity ?? []).length > 0 && (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
              >
                <div className="dashv2-panel-actitle">
                  Recent activity
                  <span className="dashv2-panel-viewall">View all</span>
                </div>
                <div className="dashv2-panel-actlist">
                  {(data.recent_activity ?? []).map((item) => (
                    <ActivityItem key={item.id} item={item} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </aside>
    </>
  );
};

export default ShiftDetailPanel;
